"use client";

import { useContext, forwardRef } from "react";
import styled from "styled-components";
import { sharedInputCss } from "../utils/sharedCss";
import { ValidityStateContext, SmallContext, IdContext, DescribedByContext, TypeContext, DisabledContext } from "../utils/context";
import { AppearanceContext } from "@churchofjesuschrist/eden-appearance";
import { jsx as _jsx } from "react/jsx-runtime";
export const InternalUseOnlyInput = styled.input.withConfig({
  componentId: "sc-cizuhu-0"
})(["", ";margin:0;&[type]{appearance:none;box-sizing:border-box;}"], sharedInputCss);

/**
 * The `Input` component allows users to input data into a form.
 * @param {object} props - The component props
 * @param {string} [props.aria-describedby] - Identifies the element (or elements) that describes the input
 * @param {string} [props.autoComplete] - Specify what if any permission the user agent has to provide automated assistance in filling out form field values [docs](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete)
 * @param {string} [props.defaultValue] - Default value for uncontrolled use
 * @param {boolean} [props.disabled] - Makes the input not mutable, focusable, or even submitted with the form
 * @param {string} [props.id] - An identifier which must be unique in the whole document
 * @param {string} [props.max] - The input value must be less than or equal to the max value
 * @param {string} [props.maxLength] - The number of characters must not exceed the maxLength value
 * @param {string} [props.min] - The input value must be greater than or equal to the min value
 * @param {string} [props.minLength] - The number of characters must not be less than the minLength value
 * @param {string} [props.name] - Used to reference the input in JavaScript, or to reference form data after a form is submitted
 * @param {Function} [props.onChange] - Callback to execute when the value changes
 * @param {string} [props.pattern] - A JavaScript regular expression ex:(^[A-Z].*)
 * @param {boolean} [props.readOnly] - Makes the element not mutable
 * @param {boolean} [props.required] - Whether the input is required to be filled out to validate
 * @param {boolean} [props.small] - A boolean indicating if the Input should be rendered in a smaller height
 * @param {"color" | "email" | "hidden" | "number" | "password" | "search" | "tel" | "text" | "url"} [props.type] - A string representing the type of input to render. If desired type is not listed, see specific components: `DateInput`, `TimeInput`, `FileInput`, etc...
 * @param {string} [props.value] - The value of the Input for controlled use
 * @param {React.Ref} ref - Ref forwarded to the underlying element.
 * @returns {React.ReactElement} The Input component
 */
const InputComponent = ({
  "aria-describedby": ariaDescribedBy,
  id,
  small,
  type = "text",
  disabled,
  ...props
}, ref) => {
  const validityStateContext = useContext(ValidityStateContext);
  const smallContext = useContext(SmallContext);
  const idContext = useContext(IdContext);
  const describedByContext = useContext(DescribedByContext);
  const curAppearanceContext = useContext(AppearanceContext);
  const typeContext = useContext(TypeContext);
  const disabledContext = useContext(DisabledContext);
  return /*#__PURE__*/_jsx(InternalUseOnlyInput, {
    "aria-describedby": ariaDescribedBy ? ariaDescribedBy : describedByContext,
    id: id ? id : idContext,
    ref: ref,
    $small: small ? small : smallContext,
    disabled: disabled ? disabled : disabledContext,
    type: typeContext ?? type,
    $validityState: validityStateContext,
    $curAppearanceContext: curAppearanceContext,
    ...props
  });
};
const Input = /*#__PURE__*/forwardRef(InputComponent);
Input.displayName = "Input";

// Custom property for the FormField to consume
Input.isInput = true;
export default Input;